import React, { useState, useRef } from 'react';
import colors from '../../utils/colors';
import typography from '../../utils/typography';
import Tooltip from './Tooltip';

const LargeButton = ({ 
  variant = 'primary', 
  children, 
  text,
  leftIcon, 
  rightIcon,
  tooltip,
  tooltipPosition = "top",
  tooltipDelay = 300,
  onClick, 
  disabled = false,
  className = "",
  // Legacy props for backwards compatibility
  icon,
  iconOnly = false,
  ...props 
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimeoutRef = useRef(null);

  // Legacy support: if icon prop is used, treat as leftIcon
  const finalLeftIcon = leftIcon || icon;
  const isIconOnly = iconOnly || (!children && !text && (finalLeftIcon || rightIcon));
  const isLabelButton = children || text;

  const getButtonStyles = () => {
    switch (variant) {
      case "primary":
        return `hover:bg-opacity-90`;
      case "secondary":
        return `hover:bg-opacity-80`;
      case "tertiary":
        return `bg-transparent border hover:bg-white/5`;
      case "aura":
        return `hover:brightness-110`;
      case "inactive":
        return `cursor-not-allowed`;
      default:
        return `hover:bg-opacity-90`;
    }
  };

  const buttonSize = isIconOnly ? "w-14 h-14" : "h-14 px-6 gap-2";

  const getBackgroundStyle = () => {
    const baseStyle = {};
    
    if (variant === "aura") {
      baseStyle.backgroundImage = "url('/aura.png')";
      baseStyle.backgroundSize = "cover";
      baseStyle.backgroundPosition = "center";
      baseStyle.backgroundRepeat = "no-repeat";
    }
    
    if (variant === "primary") {
      baseStyle.backgroundColor = colors.text.primary;
      baseStyle.color = colors.bg.primary;
    }

    if (variant === "secondary") {
      baseStyle.backgroundColor = colors.bg.secondary;
      baseStyle.color = colors.text.primary;
    }

    if (variant === "tertiary") {
      baseStyle.borderColor = colors.border.primary;
      baseStyle.color = colors.text.primary;
    }

    if (variant === "aura") {
      baseStyle.color = colors.text.primary;
    }

    if (variant === "inactive") {
      baseStyle.backgroundColor = colors.bg.tertiary;
      baseStyle.color = colors.text.inactive;
    }
    
    return baseStyle;
  };

  const getIconColor = () => {
    switch (variant) {
      case "primary":
        return colors.bg.primary || '#0E0808';
      case "secondary":
      case "tertiary":
      case "aura":
        return colors.text.primary || '#FAF7F5';
      case "inactive":
        return colors.text.inactive || '#726E6C';
      default:
        return colors.bg.primary || '#0E0808';
    }
  };

  const handleMouseEnter = () => {
    if (tooltip && !disabled) {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
      tooltipTimeoutRef.current = setTimeout(() => {
        setShowTooltip(true);
      }, tooltipDelay);
    }
  };

  const handleMouseLeave = () => {
    if (tooltip) {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
      setShowTooltip(false);
    }
  };

  const handleClick = (e) => {
    if (!disabled && onClick) {
      onClick(e);
    }
  };

  return (

      <button
        onClick={handleClick}
        disabled={disabled}
        className={`flex justify-center items-center rounded-full cursor-pointer transition-all ${buttonSize} ${getButtonStyles()} ${className}`}
        style={getBackgroundStyle()}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        {/* Left Icon */}
        {finalLeftIcon && (
          <div className="w-4 h-4 flex items-center justify-center" style={{ color: getIconColor() }}>
            {finalLeftIcon}
          </div>
        )}
        
        {/* Text/Children */}
        {isLabelButton && (
          <span style={{ ...typography.large.title }}>
            {text || children}
          </span>
        )}
        
        {/* Right Icon */}
        {rightIcon && (
          <div className="w-4 h-4 flex items-center justify-center" style={{ color: getIconColor() }}>
            {rightIcon}
          </div>
        )}
      </button>


  );
};

export default LargeButton;